import { FlexRow } from '@/components/common/Flex';
import {
    AnyFinancialEntity,
    ExternalFinancialEntity,
} from '@common/financialEntities';
import { FieldConfig } from '@common/financialEntities/config';
import { getRetirementContributionLimitContent } from '@common/react/financialEntities';
import { AmountRecurrence } from '@common/types';
import { FC } from 'react';
import Callout from '../common/Callout';
import { EntityViewGridSection } from '../common/EntityView';
import { FlexCol } from '../common/Flex';
import InputField from '../common/InputField';
import { Text } from '../common/Typography';
import { GENERIC_RETIREMENT_CONTRIBUTION_LIMIT_CONTENT } from './helpers';

export interface FinancialEntityDetailExpectedSectionProps {
    fe: ExternalFinancialEntity;
    form: Record<string, any>;
    fields: FieldConfig[];
    onFieldChange: (name: string, value: any) => void;
}

const DEFAULT_AMOUNT_RECURRENCE: AmountRecurrence = {
    amount: 0,
    recurrence: {
        freq: 'MONTHLY',
        interval: 1,
    },
};

const FinancialEntityDetailExpectedSection: FC<
    FinancialEntityDetailExpectedSectionProps
> = ({ fe, form, fields, onFieldChange }) => {
    if (fields.length === 0) return <></>;

    const isMyFE = fe.isMine;

    // Use label of first field as section title
    const title = fields[0].label;

    return (
        <EntityViewGridSection title={title} mb={12}>
            <FlexCol gap={2}>
                {fields.map((field) => {
                    const rightText = field.rightText;
                    const value =
                        field.type === 'transactionSchedulePlan'
                            ? form[field.name] ?? DEFAULT_AMOUNT_RECURRENCE
                            : form[field.name];

                    return (
                        <FlexCol key={field.name} gap={2}>
                            <FlexRow alignItems="center" gap={4}>
                                <InputField
                                    name={field.name}
                                    label={
                                        field.type === 'checkbox'
                                            ? field.label
                                            : ''
                                    }
                                    type={field.type}
                                    value={value}
                                    onChange={(value) => {
                                        onFieldChange(field.name, value);
                                    }}
                                    inputProps={{
                                        variant: 'flushed',
                                        size:
                                            field.type === 'checkbox'
                                                ? 'md'
                                                : 'lg',
                                    }}
                                    showPlusOrMinusComponent={
                                        field.showPlusOrMinusComponent
                                    }
                                    transactionScheduleInputOptions={{
                                        ...(field.transactionScheduleInputOptions ??
                                            {}),
                                        disabledText:
                                            field.name === 'plannedPayment' &&
                                            form['isPaidOffMonthly'] === 'true'
                                                ? 'Full balance'
                                                : undefined,
                                    }}
                                    isReadOnly={!isMyFE}
                                />
                                {rightText && (
                                    <Text
                                        flexBasis={1}
                                        mt={2}
                                        mr={16}
                                        whiteSpace="nowrap"
                                    >
                                        {rightText}
                                    </Text>
                                )}
                            </FlexRow>
                            <BottomContent fe={fe} field={field} />
                        </FlexCol>
                    );
                })}
            </FlexCol>
        </EntityViewGridSection>
    );
};

const BottomContent = ({
    fe,
    field,
}: {
    fe: AnyFinancialEntity;
    field: FieldConfig;
}) => {
    if (
        fe.type === 'RetirementAccount' &&
        field.name === 'expectedContribution'
    ) {
        return (
            <Callout mt={4} iconName="InfoCircle">
                {getRetirementContributionLimitContent(fe, {
                    GENERIC_RETIREMENT_CONTRIBUTION_LIMIT_CONTENT,
                })}
            </Callout>
        );
    }

    return <></>;
};

export default FinancialEntityDetailExpectedSection;
